import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import Cookies from 'universal-cookie'

import { AppThunk } from '../store'
import { getCommonPart } from '../../apis/common'
import { setErrors } from './errorsSlice'
import { CommonPart } from '../../types/common'

import { nowDate, maxDate } from '../../../utils/date'
import { PostLangStatusParams } from '../../types/setting'

interface LoginState extends CommonPart {
  isLoading: boolean
  isUpdateLoading: boolean
  updateCommonPartDay: string | undefined
  language: "ja" | "en"
}

const cookies = new Cookies()

const getLanguage = (): "ja" | "en" => {
  const language = cookies.get("language")
  if (language !== "ja" && language !== "en") {
    return "ja"
  }

  return language
}

const initialState: Partial<LoginState> = {
  isLoading: true,
  isUpdateLoading: true,
  language: getLanguage(),
}

const setLanguage = (date: "ja" | "en") => {
  cookies.set("language", date, { path: "/", expires: maxDate(), sameSite: "lax" })
}

const commonPartSlice = createSlice({
  name: 'commonPart',
  initialState,
  reducers: {
    requestStart: (state: LoginState) => {
      state.isLoading = true
      return state
    },
    requestUpdateStart: (state: LoginState) => {
      state.isUpdateLoading = true
      return state
    },
    getCommonPartSuccess: (state: LoginState, action: PayloadAction<CommonPart>) => {
      // eslint-disable-next-line no-param-reassign
      state = { ...state, ...action.payload }
      state.isLoading = false
      state.updateCommonPartDay = nowDate()
      return state
    },
    getUpdateCommonPartSuccess: (state: LoginState, action: PayloadAction<CommonPart>) => {
      // eslint-disable-next-line no-param-reassign
      state = { ...state, ...action.payload }
      state.isUpdateLoading = false
      state.updateCommonPartDay = nowDate()
      return state
    },
    requestFailure: (state: LoginState) => {
      state.isLoading = false
      return state
    },
    requestUpdateFailure: (state: LoginState) => {
      state.isUpdateLoading = false
      return state
    },
    updateReadIntroIds: (state: LoginState, action: PayloadAction<number>) => {
      state.read_first_intro_ids.push(action.payload)
      return state
    },
    updateNoticeCount: (state: LoginState) => {
      if (state.notice_count) state.notice_count -= 1
      return state
    },
    updatePayment: (state: LoginState) => {
      state.payment_finished = true
      state.trial_end_date = ''
      return state
    },
    updateCompanyLang: (state: LoginState, action: PayloadAction<PostLangStatusParams>) => {
      if (!state.company) return state
      state.company.language = action.payload.language
      return state
    },
    updateCommonMfaAuth: (state: LoginState) => {
      state.current_member.mfa_flg = true
      return state
    },
    deleteCommonMfaAuth: (state: LoginState) => {
      state.current_member.mfa_flg = false
      return state
    },
    resetState: () => initialState,
    setStateLanguage: (state: LoginState, action: PayloadAction<"ja" | "en">) => {
      setLanguage(action.payload)
      state.language = action.payload
      return state
    },
  },
})

export const {
  requestStart,
  getCommonPartSuccess,
  requestFailure,
  updateReadIntroIds,
  updateNoticeCount,
  resetState,
  updatePayment,
  requestUpdateStart,
  getUpdateCommonPartSuccess,
  requestUpdateFailure,
  updateCompanyLang,
  updateCommonMfaAuth,
  deleteCommonMfaAuth,
  setStateLanguage,
} = commonPartSlice.actions

export const commonPartReducer = commonPartSlice.reducer

export const fetchCommonPart = (): AppThunk => async (dispatch) => {
  dispatch(resetState())
  dispatch(requestStart())
  getCommonPart()
    .then((result) => {
      dispatch(getCommonPartSuccess(result))
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateCommonPart = (): AppThunk => async (dispatch) => {
  dispatch(requestUpdateStart())
  getCommonPart()
    .then((result) => {
      dispatch(getUpdateCommonPartSuccess(result))
    })
    .catch((error) => {
      dispatch(requestUpdateFailure())
      dispatch(setErrors(error))
    })
}
